import type { Timestamp } from "firebase/firestore";

import {
  LIMITS,
  isChoiceType,
  type Answer,
  type Answers,
  type FeedbackForm,
  type FeedbackQuestion,
  type FeedbackResponse,
  type QuestionType,
} from "./schemas";

type Window = Pick<FeedbackForm, "status" | "opensAt" | "closesAt">;

/**
 * Whether a form takes answers at `now`. The same test the rules and the
 * submission trigger apply, so the UI never offers a form the write would be
 * refused on.
 */
export function isAccepting(form: Window, now: Date = new Date()): boolean {
  if (form.status !== "open") return false;
  const at = now.getTime();
  if (form.opensAt && at < form.opensAt.toMillis()) return false;
  if (form.closesAt && at >= form.closesAt.toMillis()) return false;
  return true;
}

export type WindowState = "closed" | "not-yet-open" | "ended" | "accepting";

export function windowState(form: Window, now: Date = new Date()): WindowState {
  if (form.status !== "open") return "closed";
  const at = now.getTime();
  if (form.opensAt && at < form.opensAt.toMillis()) return "not-yet-open";
  if (form.closesAt && at >= form.closesAt.toMillis()) return "ended";
  return "accepting";
}

export function newId(prefix: string): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 12)
      : Math.random().toString(36).slice(2, 14);
  return `${prefix}${random}`;
}

export function newQuestion(type: QuestionType = "rating"): FeedbackQuestion {
  const question: FeedbackQuestion = {
    id: newId("q"),
    type,
    label: "",
    required: false,
  };
  if (isChoiceType(type)) {
    question.options = [
      { id: newId("o"), label: "" },
      { id: newId("o"), label: "" },
    ];
  }
  return question;
}

/** Changing type keeps id, label and required; options only for choices. */
export function withType(
  question: FeedbackQuestion,
  type: QuestionType,
): FeedbackQuestion {
  const next: FeedbackQuestion = {
    id: question.id,
    type,
    label: question.label,
    required: question.required,
  };
  if (isChoiceType(type)) {
    next.options =
      question.options && question.options.length > 0
        ? question.options
        : newQuestion(type).options;
  }
  return next;
}

export function move<T>(items: T[], index: number, delta: -1 | 1): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

function isEmptyAnswer(answer: Answer | undefined): boolean {
  if (answer === undefined) return true;
  if (typeof answer === "string") return answer.trim() === "";
  if (Array.isArray(answer)) return answer.length === 0;
  return false;
}

/**
 * The answers as the submission document carries them: unanswered optional
 * questions dropped, text trimmed, multiple choice in the form's option order,
 * and nothing for a question the form no longer has.
 */
export function cleanAnswers(
  questions: FeedbackQuestion[],
  answers: Answers,
): Answers {
  const clean: Answers = {};
  for (const question of questions) {
    const answer = answers[question.id];
    if (isEmptyAnswer(answer)) continue;
    if (typeof answer === "string") {
      clean[question.id] = question.type.endsWith("_text")
        ? answer.trim()
        : answer;
    } else if (Array.isArray(answer)) {
      const order = (question.options ?? []).map((option) => option.id);
      clean[question.id] = order.filter((id) => answer.includes(id));
    } else {
      clean[question.id] = answer;
    }
  }
  return clean;
}

/** questionId -> message, for every question the answers do not satisfy. */
export function answerErrors(
  questions: FeedbackQuestion[],
  answers: Answers,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const question of questions) {
    const answer = answers[question.id];
    if (isEmptyAnswer(answer)) {
      if (question.required) {
        errors[question.id] =
          question.type === "multiple"
            ? "Choose at least one option."
            : question.type === "rating" || question.type === "single"
              ? "Choose an answer."
              : "Please answer this question.";
      }
      continue;
    }
    if (typeof answer === "string" && question.type.endsWith("_text")) {
      const max =
        question.type === "short_text" ? LIMITS.shortText : LIMITS.longText;
      if (answer.trim().length > max) {
        errors[question.id] = `Keep it to ${max} characters.`;
      }
    }
  }
  return errors;
}

// ── Results ────────────────────────────────────────────────────────────────

export type QuestionSummary =
  | {
      kind: "rating";
      question: FeedbackQuestion;
      answered: number;
      counts: [number, number, number, number, number];
      average: number | null;
    }
  | {
      kind: "choice";
      question: FeedbackQuestion;
      answered: number;
      counts: { optionId: string; label: string; count: number }[];
    }
  | {
      kind: "text";
      question: FeedbackQuestion;
      answered: number;
      answers: { responseId: string; text: string }[];
    };

export function summarize(
  questions: FeedbackQuestion[],
  responses: FeedbackResponse[],
): QuestionSummary[] {
  return questions.map((question): QuestionSummary => {
    const given = responses
      .map((response) => ({
        responseId: response.id,
        answer: response.answers[question.id],
      }))
      .filter((entry) => !isEmptyAnswer(entry.answer));

    if (question.type === "rating") {
      const counts: [number, number, number, number, number] = [0, 0, 0, 0, 0];
      let total = 0;
      let answered = 0;
      for (const { answer } of given) {
        if (typeof answer !== "number" || answer < 1 || answer > 5) continue;
        counts[answer - 1] += 1;
        total += answer;
        answered += 1;
      }
      return {
        kind: "rating",
        question,
        answered,
        counts,
        average: answered === 0 ? null : total / answered,
      };
    }

    if (isChoiceType(question.type)) {
      const counts = (question.options ?? []).map((option) => ({
        optionId: option.id,
        label: option.label,
        count: 0,
      }));
      let answered = 0;
      for (const { answer } of given) {
        const picked = Array.isArray(answer) ? answer : [answer];
        let counted = false;
        for (const id of picked) {
          const row = counts.find((entry) => entry.optionId === id);
          if (row) {
            row.count += 1;
            counted = true;
          }
        }
        if (counted) answered += 1;
      }
      return { kind: "choice", question, answered, counts };
    }

    const answers = given
      .filter(
        (entry): entry is { responseId: string; answer: string } =>
          typeof entry.answer === "string",
      )
      .map((entry) => ({ responseId: entry.responseId, text: entry.answer }));
    return { kind: "text", question, answered: answers.length, answers };
  });
}

/** An answer as a person reads it: option labels, not ids. */
export function answerText(
  question: FeedbackQuestion,
  answer: Answer | undefined,
): string {
  if (answer === undefined) return "";
  if (isChoiceType(question.type)) {
    const ids = Array.isArray(answer) ? answer : [String(answer)];
    return ids
      .map(
        (id) =>
          question.options?.find((option) => option.id === id)?.label ?? id,
      )
      .join("; ");
  }
  return Array.isArray(answer) ? answer.join("; ") : String(answer);
}

// ── Editor ────────────────────────────────────────────────────────────────

export interface FormDraft {
  formId: string;
  title: string;
  intro: string;
  status: "open" | "closed";
  anonymous: boolean;
  /** "YYYY-MM-DDTHH:mm" on the event's clock, or "". */
  opensAt: string;
  closesAt: string;
  questions: FeedbackQuestion[];
}

/** Problems that would make SAVE_FEEDBACK_FORM fail, worded for the editor. */
export function draftProblems(
  draft: FormDraft,
  resolve: (wall: string) => Date | null,
): string[] {
  const problems: string[] = [];
  if (!draft.title.trim()) problems.push("Give the form a title.");
  if (draft.title.length > LIMITS.title)
    problems.push(`Keep the title to ${LIMITS.title} characters.`);
  if (draft.intro.length > LIMITS.intro)
    problems.push(`Keep the introduction to ${LIMITS.intro} characters.`);
  if (draft.questions.length > LIMITS.questions)
    problems.push(`A form holds at most ${LIMITS.questions} questions.`);
  if (draft.status === "open" && draft.questions.length === 0)
    problems.push("Add at least one question before opening the form.");
  draft.questions.forEach((question, index) => {
    const n = index + 1;
    if (!question.label.trim()) problems.push(`Question ${n} needs a text.`);
    if (isChoiceType(question.type)) {
      const options = question.options ?? [];
      if (options.length < LIMITS.minOptions)
        problems.push(`Question ${n} needs at least two options.`);
      if (options.some((option) => !option.label.trim()))
        problems.push(`Question ${n} has an empty option.`);
    }
  });
  const opens = draft.opensAt ? resolve(draft.opensAt) : null;
  const closes = draft.closesAt ? resolve(draft.closesAt) : null;
  if (draft.opensAt && !opens)
    problems.push("The opening time does not exist on the event's clock.");
  if (draft.closesAt && !closes)
    problems.push("The closing time does not exist on the event's clock.");
  if (opens && closes && closes <= opens)
    problems.push("The form has to close after it opens.");
  return problems;
}

/** The SAVE_FEEDBACK_FORM payload. `options` only rides choice questions. */
export function draftPayload(
  draft: FormDraft,
  resolve: (wall: string) => Date | null,
  toTimestamp: (date: Date) => Timestamp,
): Record<string, unknown> {
  const at = (wall: string) => {
    if (!wall) return null;
    const date = resolve(wall);
    return date ? toTimestamp(date) : null;
  };
  return {
    formId: draft.formId,
    title: draft.title.trim(),
    intro: draft.intro.trim(),
    status: draft.status,
    anonymous: draft.anonymous,
    opensAt: at(draft.opensAt),
    closesAt: at(draft.closesAt),
    questions: draft.questions.map((question) => {
      const base = {
        id: question.id,
        type: question.type,
        label: question.label.trim(),
        required: question.required,
      };
      return isChoiceType(question.type)
        ? {
            ...base,
            options: (question.options ?? []).map((option) => ({
              id: option.id,
              label: option.label.trim(),
            })),
          }
        : base;
    }),
  };
}
